import React, { useEffect, useRef } from 'react';
import { getSrc } from 'gatsby-plugin-image';
import styled, { createGlobalStyle, ThemeProvider } from 'styled-components';
import { useStaticQuery, graphql } from 'gatsby';
import { Helmet } from 'react-helmet';
import { I18nextProvider } from 'react-i18next';
import useDocumentPathname from '../hooks/useDocumentPathname';
import Navigation from '../components/Navigation/Navigation';
import PageFooter from '../components/PageFooter/PageFooter';


import './ws10-overrides.css';
import themeWS10 from '@legacy-vfuk/core-theme-ws10';
import i18n from '../config/i18n';

import { addSeoHeadContent, addSeoBodyContent } from '../helpers/SeoHelpers';
import { DirectionProvider } from '../contexts/DirectionContext';
import SectionBannerCarousel from '../components/pageBuilder/Sections/SectionBannerCarousel/SectionBannerCarousel';
import GlobalFloatingPill from '../components/GlobalFloatingPill/GlobalFloatingPill';

// Pointing to the assets folder previously set by the gatsby builder
themeWS10.setBaseAssetLocation('https://dev.oneportal.vodafone.com/cdn/source-web/');

const GlobalStyle = createGlobalStyle`
  ${'' /* Vodafone Brand Colors */}
  :root {
    --red: #e60000;
    --crimsonRed: #990000;
    --white: #ffffff;
    --gallery: #ebebeb;
    --DustyGray: #999999;
    --mineShaft: #333333;
    --black: #000000;
  }

  html,
  body,
  #___gatsby,
  #gatsby-focus-wrapper {
    min-height: 100%;
  }

  html {
    scroll-behavior: smooth;
    
    scroll-padding-top: 72px;
    @media (min-width: 768px) {
      scroll-padding-top: 76px;
    }
    @media (min-width: 1024px) {
      scroll-padding-top: 74px;
    }
  }

  body {
    font-family: 'Vodafone', sans;
    font-weight: 400;
    background: var(--gallery);
  }

  .disable-scrolling {
    overflow: hidden;
  }
`;

const MainLayoutWrapper = styled.div`
  height: 100%;
`;

const MainLayoutCss = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  #footer {
    margin-top: auto;
  }
`;

interface MainLayoutProps {
  locationPathname: string;
}

const MainLayout: React.FC<MainLayoutProps> = ({
  children,
  locationPathname,
}) => {
  const data = useStaticQuery(graphql`
    query SiteTitleQuery {
      contentfulPageBuilder(slug: { eq: "/" }) {
        sections {
          ... on ContentfulSectionHeroWithTextAndCta {
            mainImage {
              gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
              description
            }
            responsiveImageCollection {
              isMainImage
              imageTitle {
                title
                gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
                description
              }
            }
          }
        }
      }
      contentfulPageHome {
        hero {
          backgroundImage {
            gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
          }
        }
      }
      site {
        siteMetadata {
          title
        }
      }
      contentfulSiteConfig {
        favouriteIcon {
          gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
        }
        language
        secondLanguageUrlPrefix
        secondLanguageDirection
        metaData {
          attributes {
            name
            value
          }
        }
        scripts {
          __typename
          ... on ContentfulSeoInlineScript {
            contentful_id
            scriptLocation
            script {
              script
            }
          }
          ... on ContentfulSeoExternalScript {
            contentful_id
            scriptLocation
            runType
            src
          }
          ... on ContentfulSeoPageChangeScript {
            contentful_id
            script {
              script
            }
          }
          ... on ContentfulSeoInteractionScript {
            contentful_id
            script {
              script
            }
          }
        }
      }
    }
  `);

  const siteImage = `${getMainImageUrl(
    data?.contentfulPageBuilder?.sections ?? []
  )}`;

  const seoHead = useRef(
    addSeoHeadContent(data?.contentfulSiteConfig, [
      {
        attributes: [
          {
            name: 'og:image',
            value: siteImage,
          },
          {
            name: 'twitter:image',
            value: siteImage,
          },
        ],
      },
    ])
  );

  const pathname = useDocumentPathname();

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (window.seoPageChange) {
      try {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.seoPageChange();
      } catch (e) {
        console.error(e);
      }
    }
  }, [pathname]);

  useEffect(() => {
    addSeoBodyContent(data.contentfulSiteConfig);
  }, []);

  let useDefaultTemplate = true;
  let templateText;
  // NB - this code only decides whether to NOT add the default template.
  // The decision whether or not to add the custom template is made in html.js.
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    templateText =
      require('raw-loader!../../vhub-opco-templates/index.template.html').default;
  } catch (e) {
    templateText = '';
  }

  if (templateText && templateText.length > 0) {
    useDefaultTemplate = false;
  }

  const pathnameArray = locationPathname ? locationPathname.split('/') : [];
  const isSecondLanguageOnUrl = pathnameArray.includes(
    data.contentfulSiteConfig.secondLanguageUrlPrefix
  );
  const defaultDirection = 'ltr';
  const direction =
    isSecondLanguageOnUrl && data.contentfulSiteConfig.secondLanguageDirection
      ? data.contentfulSiteConfig.secondLanguageDirection
      : defaultDirection;
  const firstLanguageLocalCode = 'en';
  const secondLanguageUrlPrefix =
    data.contentfulSiteConfig.secondLanguageUrlPrefix;

  return (
    <I18nextProvider i18n={i18n}>
      <ThemeProvider theme={themeWS10}>
        <DirectionProvider direction={direction}>
          <themeWS10.globalStyles />
          <MainLayoutWrapper>
            <Helmet>
              <html lang="en" dir={direction} />
            </Helmet>
            {seoHead.current}
            <MainLayoutCss id="scroll-root">
              {useDefaultTemplate && (
                <Navigation
                  firstLanguageLocalCode={firstLanguageLocalCode}
                  secondLanguageUrlPrefix={secondLanguageUrlPrefix}
                  isSecondLanguageOnUrl={isSecondLanguageOnUrl}
                />
              )}
              <GlobalStyle />
              <SectionBannerCarousel />
              <GlobalFloatingPill  />
              {children}
              {useDefaultTemplate && (
                <div id="footer">
                  <PageFooter
                    firstLanguageLocalCode={firstLanguageLocalCode}
                    secondLanguageUrlPrefix={secondLanguageUrlPrefix}
                    isSecondLanguageOnUrl={isSecondLanguageOnUrl}
                  />
                </div>
              )}
            </MainLayoutCss>
          </MainLayoutWrapper>
        </DirectionProvider>
      </ThemeProvider>
    </I18nextProvider>
  );
};

function getMainImageUrl(sections: any[]) {
  // Gets the main image from a ContentfulSectionHeroWithTextAndCta.
  // It will be either the mainImage or an item in the responsive image set.
  const SECTION_HERO_TYPE = 'ContentfulSectionHeroWithTextAndCta';
  const NOT_FOUND = 'notFound';
  const filteredSectionHeroes = sections.filter(
    section => section.__typename === SECTION_HERO_TYPE
  );
  let siteImageUrl = NOT_FOUND;

  if (filteredSectionHeroes.length > 0) {
    // We have a ContentfulSectionHeroWithTextAndCta.
    // Pick the first one as it will be at the top of the page
    const sectionHero = filteredSectionHeroes[0];

    // The main image may be part of a responsive image collection.
    // Search for it in responsiveImageCollection and get its url.
    const mainResponsiveImages = sectionHero.responsiveImageCollection.filter(
      (image: any) => image.isMainImage
    );

    if (mainResponsiveImages.length > 0) {
      siteImageUrl = getSrc(mainResponsiveImages[0].imageTitle) ?? NOT_FOUND;
    } else if (sectionHero.mainImage) {
      // The image is in the mainImage. Attempt to get its url.
      const siteImageSrc = getSrc(sectionHero.mainImage) as string;
      siteImageUrl = siteImageSrc.split('?')[0] ?? NOT_FOUND;
    }
  }
  return siteImageUrl;
}

export default MainLayout;
